import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { captureError } from '../common/sentry';
import { getManifestPartialReceiptStatus, saveManifestPartialReceipt } from '../queries/queries';

export type ManifestPartialReceiptLineInput = {
  asn: string;
  poLineId: string;
  itemId?: string | null;
  sourcePartId?: string | null;
  packageId?: string | null;
  selectedForReceive?: boolean;
  requestedQuantity?: number | null;
  processedQuantity?: number | null;
  receiveQuantity?: number | null;
  status?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
};

export type SaveManifestPartialReceiptInput = {
  manifestId: string;
  tripId?: string | null;
  legId?: string | null;
  triggeredBy?: string | null;
  clientRequestId?: string | null;
  lines: ManifestPartialReceiptLineInput[];
};

export type ManifestPartialReceiptLineStatus = {
  id: string;
  sessionId: string;
  manifestId: string;
  triggeredBy: string | null;
  ssccId?: string | null;
  asn: string | null;
  poLineId: string | null;
  itemId: string | null;
  sourcePartId?: string | null;
  lineDescription?: string | null;
  packageId: string | null;
  selectedForReceive: boolean;
  requestedQuantity: number | null;
  processedQuantity: number | null;
  receiveQuantity: number | null;
  packageQuantity: number | null;
  orderedQuantity: number | null;
  receivedQuantity: number | null;
  status: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  processedAt: string | null;
};

export type ManifestPartialReceiptStatusResult = {
  manifestId: string;
  lines: ManifestPartialReceiptLineStatus[];
};

type SaveManifestPartialReceiptMutation = {
  saveManifestPartialReceipt: ManifestPartialReceiptStatusResult;
};

type GetManifestPartialReceiptStatusQuery = {
  getManifestPartialReceiptStatus: ManifestPartialReceiptStatusResult;
};

export async function saveManifestPartialReceiptRequest(
  input: SaveManifestPartialReceiptInput
): Promise<ManifestPartialReceiptStatusResult> {
  const runMutation = async (mutationInput: SaveManifestPartialReceiptInput) => {
    return (await API.graphql(
      graphqlOperation(saveManifestPartialReceipt, { input: mutationInput })
    )) as GraphQLResult<SaveManifestPartialReceiptMutation>;
  };

  const inputWithoutSourcePartId: SaveManifestPartialReceiptInput = {
    ...input,
    lines: input.lines.map(({ sourcePartId, ...line }) => line)
  };

  try {
    const result = await runMutation(inputWithoutSourcePartId);

    if (!result.data?.saveManifestPartialReceipt) {
      throw new Error('No data returned from saveManifestPartialReceipt');
    }

    return result.data.saveManifestPartialReceipt;
  } catch (error) {
    captureError(error, 'manifestPartialReceipt-save');
    throw error;
  }
}

export async function getManifestPartialReceiptStatusRequest(
  manifestId: string
): Promise<ManifestPartialReceiptStatusResult> {
  try {
    const result = (await API.graphql(
      graphqlOperation(getManifestPartialReceiptStatus, { manifestId })
    )) as GraphQLResult<GetManifestPartialReceiptStatusQuery>;

    if (!result.data?.getManifestPartialReceiptStatus) {
      throw new Error('No data returned from getManifestPartialReceiptStatus');
    }

    return result.data.getManifestPartialReceiptStatus;
  } catch (error) {
    captureError(error, 'manifestPartialReceipt-getStatus');
    throw error;
  }
}
